import { IconPencilOff } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  Tooltip,
  Typography,
} from '@cfa/react-components';

import {
  mapCategoryToAliasTranslation,
  mapCategoryToColor,
  mapCategoryToIcon,
} from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';
import { cn } from '@/utils/tailwind';

import { Plan } from '@/_generated/api-clients/xp';
import { sourceDataFamily } from '@/atoms/data-view';

interface TemplateCardProps {
  onCopy: () => void;
  onPreview: (id: string) => void;
  plan: Plan;
  onAdd: () => void;
  isLoading?: boolean;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  onCopy,
  onPreview,
  plan,
  onAdd,
  isLoading,
}) => {
  const { t } = useTranslation();
  const managedPlans = useAtomValue(sourceDataFamily<Plan>('manage-plans'));
  const isAlreadyAdded = managedPlans.some(p => p.id === plan.id);

  return (
    <Card
      className="animate-fade-in no-hover relative m-0 flex h-auto cursor-default! flex-row! overflow-visible! p-0!"
      data-testid="TemplatePlanCard"
    >
      <div
        className="flex h-auto w-[80px] shrink-0 items-center justify-center rounded-l-[inherit]"
        style={{
          backgroundColor: mapCategoryToColor(
            plan.category,
            plan.status === 'INACTIVE',
          ),
        }}
      >
        {mapCategoryToIcon(plan.category)}
      </div>

      <CardContent
        className={cn(
          'flex w-full min-w-0 flex-col gap-3 p-3! sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:p-4!',
        )}
      >
        <div className="min-w-0 flex-1 overflow-hidden text-start">
          <Typography variant="overline3">
            {t(mapCategoryToAliasTranslation(plan.category))}
          </Typography>

          <Typography
            className="line-clamp-2 overflow-hidden text-left wrap-break-word text-ellipsis"
            data-testid="PlanName"
            fontWeight="bold"
            variant="body1"
          >
            {itemByLanguage(plan.name)}
          </Typography>
          <Typography data-testid="PlanTaskCount" variant="body2">
            {`${plan.totalSteps} ${t('Generic.items')}`}
          </Typography>
        </div>

        <div className="flex flex-none items-center gap-2 md:justify-end md:gap-4">
          {plan.readonly && (
            <Tooltip
              content={t('TrainingPlans.templates.readonlyTooltip')}
              placement="bottom-end"
              showOnElementEvents={['hover']}
            >
              <IconPencilOff
                aria-label={t('TrainingPlans.templates.readonlyTooltip')}
                className="text-grayscale-gray3! hidden shrink-0! md:block"
                size={24}
              />
            </Tooltip>
          )}
          <Button
            color="secondary"
            className="w-24! min-w-24! md:min-w-20!"
            data-testid="templatePreviewButtonTest"
            onClick={() => {
              if (plan.id) {
                onPreview(plan.id);
              }
            }}
            variant="outlined"
          >
            {t('Button.preview')}
          </Button>
          <Button
            disabled={plan.readonly && isAlreadyAdded}
            loading={isLoading}
            className="w-[88px]! min-w-[88px]! md:min-w-16!"
            color="secondary"
            data-testid="templateCopyButtonTest"
            onClick={plan.readonly ? onAdd : onCopy}
            variant="filled"
          >
            {plan.readonly
              ? isAlreadyAdded
                ? t('Button.added')
                : t('Button.add')
              : t('Button.copy')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
